@if (form) {
  <div class="recurrence" [formGroup]="form">
    <mat-slide-toggle formControlName="enabled" class="recurrence-toggle">
      Repeat this appointment
    </mat-slide-toggle>

    @if (enabled) {
      <div class="recurrence-body">
        <div class="recurrence-row">
          <mat-form-field appearance="outline" class="recurrence-field-sm">
            <mat-label>Repeat every</mat-label>
            <input matInput type="number" formControlName="interval" min="1" [max]="maxInterval">
          </mat-form-field>

          <mat-form-field appearance="outline" class="recurrence-field">
            <mat-label>Frequency</mat-label>
            <mat-select formControlName="frequency">
              <mat-option value="Daily">{{ form.get('interval').value > 1 ? 'days' : 'day' }}</mat-option>
              <mat-option value="Weekly">{{ form.get('interval').value > 1 ? 'weeks' : 'week' }}</mat-option>
              <mat-option value="Monthly">{{ form.get('interval').value > 1 ? 'months' : 'month' }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        @if (intervalAboveLimit) {
          <div class="recurrence-error">
            A {{ frequency.toLowerCase() }} repeat can be at most every {{ maxInterval }}
            {{ intervalUnit }}; anything longer cannot repeat within {{ horizonDays }} days.
          </div>
        }

        @if (frequency === 'Weekly') {
          <div class="recurrence-block">
            <div class="recurrence-label">On these days</div>
            <div class="recurrence-days">
              @for (day of weekdays; track day) {
                <mat-checkbox
                  [checked]="isWeekdaySelected(day)"
                  (change)="toggleWeekday(day, $event.checked)">
                  {{ day.slice(0, 3) }}
                </mat-checkbox>
              }
            </div>
            <div class="recurrence-hint">
              Leave all unticked to repeat on the same weekday the appointment starts.
            </div>
          </div>
        }

        @if (frequency === 'Monthly') {
          <div class="recurrence-block">
            <mat-radio-group formControlName="monthlyMode" class="recurrence-modes">
              <mat-radio-button value="ByDayOfMonth">On a day of the month</mat-radio-button>
              <mat-radio-button value="ByNthWeekday">On a weekday of the month</mat-radio-button>
            </mat-radio-group>

            @if (monthlyMode === 'ByDayOfMonth') {
              <mat-form-field appearance="outline" class="recurrence-field-sm">
                <mat-label>Day</mat-label>
                <input matInput type="number" formControlName="monthlyDayOfMonth" min="1" max="31">
              </mat-form-field>
              <!-- A sibling rather than a mat-hint: the field is 130px wide, so this wraps to
                   three lines inside a subscript sized for one and lands on top of the block
                   below. Matches how the weekly hint is already done. -->
              <div class="recurrence-hint">Months without this day are skipped.</div>
            } @else {
              <div class="recurrence-row">
                <mat-form-field appearance="outline" class="recurrence-field">
                  <mat-label>Which</mat-label>
                  <mat-select formControlName="monthlyNth">
                    @for (nth of nthWeeks; track nth) {
                      <mat-option [value]="nth">{{ nth }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="recurrence-field">
                  <mat-label>Weekday</mat-label>
                  <mat-select formControlName="monthlyWeekday">
                    @for (day of weekdays; track day) {
                      <mat-option [value]="day">{{ day }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
            }
          </div>
        }

        <div class="recurrence-block">
          <div class="recurrence-label">Ends</div>
          <mat-radio-group formControlName="endsMode" class="recurrence-modes">
            <mat-radio-button value="Count">After a number of appointments</mat-radio-button>
            <mat-radio-button value="Date">On a date</mat-radio-button>
          </mat-radio-group>

          @if (endsMode === 'Count') {
            <mat-form-field appearance="outline" class="recurrence-field-sm">
              <mat-label>Appointments</mat-label>
              <input matInput type="number" formControlName="endsAfterCount" min="1" [max]="maxOccurrences">
            </mat-form-field>
            @if (countBeyondHorizon) {
              <div class="recurrence-error">
                This pattern only fits {{ occurrencesThatFit }} appointments within
                {{ horizonDays }} days of the first one. Reduce the number of appointments
                or choose a more frequent repeat.
              </div>
            }
          } @else {
            <mat-form-field appearance="outline" class="recurrence-field">
              <mat-label>Last appointment on or before</mat-label>
              <input matInput [matDatepicker]="endsPicker" formControlName="endsOnDate"
                     [min]="startDate()" [max]="latestEndDate">
              <mat-datepicker-toggle matIconSuffix [for]="endsPicker"></mat-datepicker-toggle>
              <mat-datepicker #endsPicker></mat-datepicker>
            </mat-form-field>
            @if (endsBeforeStart) {
              <div class="recurrence-error">
                The end date cannot be before the appointment itself.
              </div>
            }
            @if (endsBeyondHorizon) {
              <div class="recurrence-error">
                A repeat must finish within {{ horizonDays }} days of its first appointment.
                Choose a date on or before {{ latestEndDate | date:'d MMM yyyy' }}.
              </div>
            }
            @if (dateMakesTooMany) {
              <div class="recurrence-error">
                This pattern makes more than {{ maxOccurrences }} appointments before that
                date. Choose an earlier end date or a less frequent repeat.
              </div>
            }
          }
        </div>

        <div class="recurrence-hint recurrence-horizon">
          <mat-icon inline>info_outline</mat-icon>
          A repeat must finish within {{ horizonDays }} days of the first appointment, and can
          create at most {{ maxOccurrences }} appointments.
        </div>
      </div>
    }
  </div>
}
